import {
  Index,
  IndexDeletionResponse,
  IndexDeletionStatus,
} from '../services/api';
import { sortIndexes } from './indexSort';

export interface PendingIndexDeletion {
  deletion_id: string;
  status: IndexDeletionStatus;
  retry_after_seconds?: number | null;
  index_name: string;
  initiated_at: string;
  outcome_uncertain?: boolean;
}

export type PendingIndexDeletionMap = Record<string, PendingIndexDeletion>;

export type UncertainIndexDeletionReconciliation =
  | {
      status: 'retry';
    }
  | {
      status: 'resolved';
      indexes: Index[];
      presentIndexIds: string[];
      absentIndexIds: string[];
    };

export const reconcileUncertainIndexDeletion404s = async (
  indexIds: readonly string[],
  loadAuthoritativeIndexes: () => Promise<Index[]>,
): Promise<UncertainIndexDeletionReconciliation> => {
  try {
    const indexes = await loadAuthoritativeIndexes();
    const authoritativeIndexIds = new Set(
      indexes.map((index) => index.index_id),
    );
    return {
      status: 'resolved',
      indexes,
      presentIndexIds: indexIds.filter((indexId) => (
        authoritativeIndexIds.has(indexId)
      )),
      absentIndexIds: indexIds.filter((indexId) => (
        !authoritativeIndexIds.has(indexId)
      )),
    };
  } catch {
    return { status: 'retry' };
  }
};

export const excludeAcceptedIndexDeletions = (
  indexes: Index[],
  acceptedDeletionIds: ReadonlySet<string>,
): Index[] => indexes.filter(
  (index) => !acceptedDeletionIds.has(index.index_id),
);

export interface UsableIndexRefreshState {
  usableIndexes: Index[];
  selectedIndex: Index | null;
}

export const deriveUsableIndexRefreshState = (
  indexes: Index[],
  acceptedDeletionIds: ReadonlySet<string>,
  requestedSelectionId?: string | null,
): UsableIndexRefreshState => {
  const usableIndexes = excludeAcceptedIndexDeletions(
    indexes,
    acceptedDeletionIds,
  );
  const requestedSelection = requestedSelectionId
    ? usableIndexes.find((index) => index.index_id === requestedSelectionId)
    : undefined;

  return {
    usableIndexes,
    selectedIndex: requestedSelection
      || sortIndexes(usableIndexes.filter((index) => !index.is_default && index.index_id !== 'playground'), 'date', false)[0]
      || sortIndexes(usableIndexes.filter((index) => index.is_default), 'date', false)[0]
      || null,
  };
};

export const recordAcceptedIndexDeletion = (
  current: PendingIndexDeletionMap,
  response: IndexDeletionResponse,
  indexName: string,
  initiatedAt: string,
): PendingIndexDeletionMap => {
  const next = { ...current };
  if (response.status === 'deleted') {
    delete next[response.index_id];
    return next;
  }
  next[response.index_id] = {
    deletion_id: response.deletion_id,
    status: response.status,
    retry_after_seconds: response.retry_after_seconds,
    index_name: indexName,
    initiated_at: initiatedAt,
  };
  return next;
};

export const recordUncertainIndexDeletion = (
  current: PendingIndexDeletionMap,
  indexId: string,
  indexName: string,
  initiatedAt: string,
): PendingIndexDeletionMap => ({
  ...current,
  [indexId]: {
    deletion_id: `uncertain:${indexId}:${initiatedAt}`,
    status: 'draining',
    retry_after_seconds: 5,
    index_name: indexName,
    initiated_at: initiatedAt,
    outcome_uncertain: true,
  },
});

export const applyIndexDeletionStatus = (
  current: PendingIndexDeletionMap,
  response: IndexDeletionResponse,
): PendingIndexDeletionMap => {
  const existing = current[response.index_id];
  if (
    !existing
    || (
      existing.deletion_id !== response.deletion_id
      && !existing.outcome_uncertain
    )
  ) {
    return current;
  }
  const next = { ...current };
  if (response.status === 'deleted') {
    delete next[response.index_id];
    return next;
  }
  next[response.index_id] = {
    ...existing,
    deletion_id: response.deletion_id,
    status: response.status,
    retry_after_seconds: response.retry_after_seconds,
    outcome_uncertain: false,
  };
  return next;
};

export const clearIndexDeletionIfMatching = (
  current: PendingIndexDeletionMap,
  indexId: string,
  deletionId: string,
): PendingIndexDeletionMap => {
  if (current[indexId]?.deletion_id !== deletionId) {
    return current;
  }
  const next = { ...current };
  delete next[indexId];
  return next;
};
